import * as THREE from 'three'
import {
  type CompiledMesh,
  type CompiledWorldChunk,
  type ProceduralMaterialId,
} from '../../chunk-schema/src/index.ts'
import {
  chunkDistanceToWorldPoint,
  sampleChunkTerrain,
  sampleChunkTerrainValidity,
} from './chunk-sampling.ts'
import { isRuntimeMeshKind, RUNTIME_MESH_KINDS, type RuntimeMeshKind } from './runtime-contract.ts'
import {
  createBlockingStructureSegments,
  type BlockingStructureSegment,
} from './structure-collision.ts'
import {
  ChunkRoadDebugOverlay,
  type RoadDebugPage,
  type RoadDebugStats,
  type RoadDebugSurface,
} from './road-debug-overlay.ts'
import { SurfaceTriangleHeightIndex } from './surface-height-index.ts'

export type SurfaceCoverage = {
  readonly meshId: string
  readonly kind: string
  readonly triangleIndex: number
  readonly height: number
  readonly mounted: boolean
}

const meshPriority: Readonly<Record<string, number>> = {
  terrain: 0,
  road: 1,
  junction: 1,
}

function isCriticalSurface(mesh: CompiledMesh) {
  return isRuntimeMeshKind(mesh.kind)
}

function geometryFromCompiled(mesh: CompiledMesh) {
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3))
  if (mesh.normals) geometry.setAttribute('normal', new THREE.BufferAttribute(mesh.normals, 3))
  else if (mesh.topology === 'triangles') geometry.computeVertexNormals()
  if (mesh.uvs) geometry.setAttribute('uv', new THREE.BufferAttribute(mesh.uvs, 2))
  if (mesh.colors) geometry.setAttribute('color', new THREE.BufferAttribute(mesh.colors, 4, true))
  geometry.setIndex(new THREE.BufferAttribute(mesh.indices, 1))
  return geometry
}

export class ChunkInstance {
  readonly group = new THREE.Group()
  readonly blockingStructureSegments: readonly BlockingStructureSegment[]

  private readonly materials: ReadonlyMap<ProceduralMaterialId, THREE.Material>
  private readonly pendingMeshes: CompiledMesh[]
  private readonly criticalMeshIds: ReadonlySet<string>
  private readonly mountedCriticalMeshIds = new Set<string>()
  private readonly mountedKinds = new Set<RuntimeMeshKind>()
  private readonly ownedGeometries: THREE.BufferGeometry[] = []
  private readonly surfaceHeightIndex = new SurfaceTriangleHeightIndex()
  private readonly debugSurfaces = new Map<string, RoadDebugSurface>()
  private roadDebug?: ChunkRoadDebugOverlay
  private debugPage: RoadDebugPage = 0
  private disposalDetached = false

  constructor(
    readonly data: CompiledWorldChunk,
    readonly macroOffset: THREE.Vector2,
    materials: ReadonlyMap<ProceduralMaterialId, THREE.Material>,
  ) {
    this.materials = materials
    this.pendingMeshes = data.meshes
      .filter((mesh) => isRuntimeMeshKind(mesh.kind))
      .sort((left, right) => (meshPriority[left.kind] ?? 2) - (meshPriority[right.kind] ?? 2) || left.id.localeCompare(right.id))
    this.criticalMeshIds = new Set(this.pendingMeshes.filter(isCriticalSurface).map((mesh) => mesh.id))
    this.blockingStructureSegments = createBlockingStructureSegments(data.colliders, macroOffset.x, macroOffset.y)
    this.group.name = `CHUNK_${data.id.zoom}_${data.id.x}_${data.id.y}`
    this.group.position.set(macroOffset.x, 0, macroOffset.y)
  }

  get hasPendingMeshes() {
    return this.pendingMeshes.length > 0
  }

  get hasPendingCriticalSurfaces() {
    return this.pendingMeshes.some(isCriticalSurface)
  }

  /** True only when every simulation-visible surface has a mounted peer. */
  get isReadyForPublication() {
    return !this.hasPendingCriticalSurfaces
      && [...this.criticalMeshIds].every((meshId) => this.mountedCriticalMeshIds.has(meshId))
  }

  get mountedMeshKinds(): readonly RuntimeMeshKind[] {
    return RUNTIME_MESH_KINDS.filter((kind) => this.mountedKinds.has(kind))
  }

  get debugStats(): Readonly<RoadDebugStats> {
    return this.roadDebug?.stats ?? {
      splineSegments: 0,
      nodes: 0,
      roadTriangles: 0,
      junctionTriangles: 0,
    }
  }

  setDebugPage(page: RoadDebugPage): void {
    this.debugPage = page
    if (page === 0) {
      this.roadDebug?.setPage(0)
      return
    }
    if (!this.roadDebug) {
      this.roadDebug = new ChunkRoadDebugOverlay(this.data, this.debugSurfaces.values())
      this.group.add(this.roadDebug.root)
    }
    this.roadDebug.setPage(page)
  }

  mountNextMesh() {
    const compiled = this.pendingMeshes[0]
    if (!compiled) return false
    const mounted = this.mountCompiledMesh(compiled)
    this.pendingMeshes.shift()
    return mounted
  }

  mountNextCriticalSurface() {
    const index = this.pendingMeshes.findIndex(isCriticalSurface)
    if (index < 0) return false
    const compiled = this.pendingMeshes[index]
    const mounted = this.mountCompiledMesh(compiled)
    this.pendingMeshes.splice(index, 1)
    return mounted
  }

  /** Build the complete terrain/road/collider-visible presentation off-scene. */
  mountAllCriticalSurfaces() {
    let mounted = false
    while (this.hasPendingCriticalSurfaces) mounted = this.mountNextCriticalSurface() || mounted
    return mounted
  }

  mountCriticalSurface() {
    return this.mountAllCriticalSurfaces()
  }

  mountMeshById(meshId: string) {
    if (this.group.getObjectByName(meshId)) return true
    const index = this.pendingMeshes.findIndex((mesh) => mesh.id === meshId)
    if (index < 0) return false
    const compiled = this.pendingMeshes[index]
    const mounted = this.mountCompiledMesh(compiled)
    this.pendingMeshes.splice(index, 1)
    return mounted
  }

  private mountCompiledMesh(compiled: CompiledMesh) {
    const material = this.materials.get(compiled.material)
    if (!material) throw new Error(`Missing runtime material ${compiled.material} for critical chunk mesh ${compiled.id}.`)
    const geometry = geometryFromCompiled(compiled)
    this.ownedGeometries.push(geometry)
    const object = compiled.topology === 'lines'
      ? new THREE.LineSegments(geometry, material)
      : new THREE.Mesh(geometry, material)
    object.name = compiled.id
    object.frustumCulled = false
    object.receiveShadow = true
    object.castShadow = false
    this.group.add(object)
    if ((compiled.kind === 'road' || compiled.kind === 'junction') && compiled.topology === 'triangles') {
      this.surfaceHeightIndex.addMesh(compiled)
    }
    this.mountedKinds.add(compiled.kind as RuntimeMeshKind)
    if (isCriticalSurface(compiled)) this.mountedCriticalMeshIds.add(compiled.id)
    if ((compiled.kind === 'road' || compiled.kind === 'junction') && compiled.topology === 'triangles') {
      const surface: RoadDebugSurface = {
        id: compiled.id,
        kind: compiled.kind,
        geometry,
        triangleCount: compiled.indices.length / 3,
      }
      this.debugSurfaces.set(compiled.id, surface)
      this.roadDebug?.addSurface(surface)
      this.roadDebug?.setPage(this.debugPage)
    }
    return true
  }

  distanceToWorldPoint(x: number, z: number) {
    return chunkDistanceToWorldPoint(this.data, { x: this.macroOffset.x, z: this.macroOffset.y }, x, z)
  }

  sampleTerrainWorld(x: number, z: number) {
    return sampleChunkTerrain(this.data, { x: this.macroOffset.x, z: this.macroOffset.y }, x, z)
  }

  sampleTerrainValidityWorld(x: number, z: number) {
    return sampleChunkTerrainValidity(this.data, { x: this.macroOffset.x, z: this.macroOffset.y }, x, z)
  }

  surfaceCoverageAtWorld(x: number, z: number, referenceHeight?: number): SurfaceCoverage | undefined {
    const localX = x - this.macroOffset.x
    const localZ = z - this.macroOffset.y
    const selectorHeight = referenceHeight ?? this.sampleTerrainWorld(x, z) ?? 0
    const hit = this.surfaceHeightIndex.sample(localX, localZ, selectorHeight)
    return hit === undefined
      ? undefined
      : {
          ...hit,
          mounted: true,
        }
  }

  disposeStep(maximumWorkItems = 192) {
    if (!this.disposalDetached) {
      this.roadDebug?.dispose()
      this.roadDebug = undefined
      this.group.clear()
      this.pendingMeshes.length = 0
      this.mountedKinds.clear()
      this.mountedCriticalMeshIds.clear()
      this.surfaceHeightIndex.clear()
      this.debugSurfaces.clear()
      this.disposalDetached = true
    }
    let remaining = Math.max(4, maximumWorkItems)
    while (this.ownedGeometries.length > 0 && remaining > 0) {
      this.ownedGeometries.pop()!.dispose()
      remaining -= 1
    }
    return this.ownedGeometries.length === 0
  }

  dispose() {
    while (!this.disposeStep(8_192)) {
      // Synchronous escape hatch for explicit teardown; streaming uses the
      // bounded disposeStep path above.
    }
  }

}
